// Financiero › Reportes. Dos reportes que el motor ya sabía calcular pero que
// solo existían como PESTAÑAS dentro de "Cartera por Cobrar"/"por Pagar": quien
// necesita la antigüedad de saldos para el cierre no entra a una pantalla de
// gestión de cobros a buscarla.
//
// No duplican el cálculo —consumen las mismas RPC— y añaden lo que una pantalla
// operativa no puede dar: la POSICIÓN NETA, que exige mirar cobrar y pagar a la
// vez.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock, FileText, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, useMarcaEmpresa, type Celda } from '@/lib/utils/exportarTabla';
import { fechaLocal } from '@/lib/utils/fecha';
import { htmlEstadoCuenta } from './estadoCuentaPdf';
import { LOCALE } from '@/lib/utils/regional';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const finRpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body, FIN)).data;

const campo = 'h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const usd = (v: unknown) => Number(v ?? 0).toLocaleString(LOCALE, { style: 'currency', currency: 'USD' });

interface AgingFila {
  partner: string; identificacion: string | null; facturas: number;
  por_vencer: number; d30: number; d60: number; d90: number; d90mas: number; total: number;
}
interface Aging { partners: AgingFila[]; totales: Record<string, number> }

// Los tramos, en el orden en que se leen: lo que aún no vence primero y lo más
// podrido al final.
const TRAMOS = [
  { k: 'por_vencer', label: 'Por vencer' },
  { k: 'd30', label: '1 a 30' },
  { k: 'd60', label: '31 a 60' },
  { k: 'd90', label: '61 a 90' },
  { k: 'd90mas', label: 'Más de 90' },
] as const;

const AGRUPAR = [
  { v: 'partner', label: 'Por cliente o proveedor' },
  { v: 'proyecto', label: 'Por proyecto' },
  { v: 'centro', label: 'Por centro de costo' },
];

// ======================================================= ANTIGÜEDAD DE SALDOS =
export function ReporteAging() {
  const [lado, setLado] = useState<'cxc' | 'cxp'>('cxc');
  const [agrupar, setAgrupar] = useState('partner');
  // FECHA DE CORTE: la cartera del 31 de diciembre reconstruida como estaba ese
  // día, sin descontar los cobros de enero. Es el papel de trabajo nº 1 de toda
  // auditoría y hasta ahora era irreproducible.
  const [corte, setCorte] = useState(() => new Date().toISOString().slice(0, 10));
  const esHoy = corte === new Date().toISOString().slice(0, 10);
  const marca = useMarcaEmpresa();

  // se piden LOS DOS lados siempre: la posición neta es el valor que este
  // reporte añade sobre la pestaña que ya existía dentro de Cartera
  const cobrar = useQuery<Aging>({
    queryKey: ['aging', 'cxc', agrupar, corte],
    queryFn: () => finRpc<Aging>('cxc_aging', { _agrupar: agrupar, _corte: corte }),
  });
  const pagar = useQuery<Aging>({
    queryKey: ['aging', 'cxp', agrupar, corte],
    queryFn: () => finRpc<Aging>('cxp_aging', { _agrupar: agrupar, _corte: corte }),
  });

  const activo = lado === 'cxc' ? cobrar : pagar;
  const filas = useMemo(() => activo.data?.partners ?? [], [activo.data]);
  const cargando = cobrar.isFetching || pagar.isFetching;

  const tot = (q: typeof cobrar, k: string) => Number(q.data?.totales?.[k] ?? 0);
  const etiqueta = lado === 'cxc' ? 'cliente' : 'proveedor';

  const headers = ['Nombre', 'Identificación', 'Facturas', ...TRAMOS.map((t) => t.label), 'Total'];
  const filasExport = useMemo<Celda[][]>(
    () => filas.map((f) => [
      f.partner, f.identificacion ?? '', f.facturas,
      ...TRAMOS.map((t) => Number(f[t.k as keyof AgingFila] ?? 0) as Celda),
      f.total,
    ]),
    [filas],
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Antigüedad de Saldos</h1>
        {cargando && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
        <span className="ml-auto text-xs text-muted-foreground">
          {esHoy ? `Al ${new Date().toLocaleDateString(LOCALE)}`
                 : `Saldos reconstruidos al ${corte} — sin los cobros posteriores`}
        </span>
      </div>

      {/* Posición neta: lo único que no se puede ver desde la pestaña de Cartera */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Posición neta</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="py-2 pr-3 text-left"></th>
                {TRAMOS.map((t) => <th key={t.k} className="whitespace-nowrap px-3 py-2 text-right">{t.label}</th>)}
                <th className="whitespace-nowrap border-l px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b">
                <td className="py-1.5 pr-3 whitespace-nowrap">Te deben</td>
                {TRAMOS.map((t) => <td key={t.k} className="px-3 py-1.5 text-right tabular-nums">{usd(tot(cobrar, t.k))}</td>)}
                <td className="border-l px-3 py-1.5 text-right font-medium tabular-nums">{usd(tot(cobrar, 'total'))}</td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 pr-3 whitespace-nowrap">Debes</td>
                {TRAMOS.map((t) => <td key={t.k} className="px-3 py-1.5 text-right tabular-nums">{usd(tot(pagar, t.k))}</td>)}
                <td className="border-l px-3 py-1.5 text-right font-medium tabular-nums">{usd(tot(pagar, 'total'))}</td>
              </tr>
              <tr className="bg-muted/60 font-semibold">
                <td className="py-2 pr-3 whitespace-nowrap">Neto</td>
                {TRAMOS.map((t) => {
                  const n = tot(cobrar, t.k) - tot(pagar, t.k);
                  return <td key={t.k} className={`px-3 py-2 text-right tabular-nums ${n < 0 ? 'text-destructive' : ''}`}>{usd(n)}</td>;
                })}
                <td className={`border-l px-3 py-2 text-right tabular-nums ${tot(cobrar, 'total') - tot(pagar, 'total') < 0 ? 'text-destructive' : ''}`}>
                  {usd(tot(cobrar, 'total') - tot(pagar, 'total'))}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-xs text-muted-foreground">
            En rojo, los tramos donde debes más de lo que te deben.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-3 text-base">
            <div className="flex overflow-hidden rounded-md border">
              {([['cxc', 'Por cobrar'], ['cxp', 'Por pagar']] as const).map(([k, l]) => (
                <button key={k} onClick={() => setLado(k)}
                  className={`px-3 py-1 text-sm ${lado === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  {l}
                </button>
              ))}
            </div>
            <select className={campo} value={agrupar} onChange={(e) => setAgrupar(e.target.value)}>
              {AGRUPAR.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </select>
            <label className="flex items-center gap-1.5 text-sm font-normal">
              <span className="text-muted-foreground">Al</span>
              <input type="date" className={campo} value={corte}
                onChange={(e) => setCorte(e.target.value || new Date().toISOString().slice(0, 10))} />
            </label>
            {!esHoy && (
              <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-semibold text-amber-600"
                title="Saldos reconstruidos a esa fecha: no se descuentan los cobros posteriores">
                reconstruida al {corte}
              </span>
            )}
            <span className="text-sm font-normal text-muted-foreground">{filas.length} fila(s)</span>
            <div className="ml-auto">
              <ExportarBotones
                nombre={`antiguedad-saldos-${lado}-${corte}`}
                titulo={`Antigüedad de saldos · ${lado === 'cxc' ? 'Por cobrar' : 'Por pagar'}`}
                subtitulo={`${AGRUPAR.find((a) => a.v === agrupar)?.label} · al ${corte}`}
                headers={headers} filas={filasExport} colsDerecha={[2, 3, 4, 5, 6, 7, 8]}
                marca={marca}
                fmtPDF={(v, c) => (c < 2 ? String(v ?? '') : c === 2 ? String(v ?? '') : usd(v))}
                disabled={cargando} />
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {!cargando && filas.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Sin saldos pendientes {lado === 'cxc' ? 'por cobrar' : 'por pagar'}.
            </p>
          )}
          {filas.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left">{agrupar === 'partner' ? `Nombre del ${etiqueta}` : 'Nombre'}</th>
                  <th className="px-3 py-2 text-right">Facturas</th>
                  {TRAMOS.map((t) => <th key={t.k} className="whitespace-nowrap px-3 py-2 text-right">{t.label}</th>)}
                  <th className="whitespace-nowrap border-l px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => (
                  <tr key={`${f.identificacion ?? f.partner}-${i}`} className="border-b last:border-0">
                    <td className="py-1.5 pr-3">
                      {f.partner}
                      {f.identificacion && <span className="ml-2 font-mono text-xs text-muted-foreground">{f.identificacion}</span>}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-muted-foreground">{f.facturas}</td>
                    {TRAMOS.map((t) => {
                      const v = Number(f[t.k as keyof AgingFila] ?? 0);
                      return (
                        <td key={t.k} className={`px-3 py-1.5 text-right tabular-nums ${v > 0 && t.k === 'd90mas' ? 'text-destructive' : ''}`}>
                          {v ? usd(v) : <span className="text-muted-foreground">—</span>}
                        </td>
                      );
                    })}
                    <td className="border-l px-3 py-1.5 text-right font-medium tabular-nums">{usd(f.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-muted/60 font-semibold">
                  <td className="py-2 pr-3">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums">{filas.reduce((a, f) => a + Number(f.facturas || 0), 0)}</td>
                  {TRAMOS.map((t) => (
                    <td key={t.k} className="px-3 py-2 text-right tabular-nums">
                      {usd(filas.reduce((a, f) => a + Number(f[t.k as keyof AgingFila] || 0), 0))}
                    </td>
                  ))}
                  <td className="border-l px-3 py-2 text-right tabular-nums">
                    {usd(filas.reduce((a, f) => a + Number(f.total || 0), 0))}
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============================================================ ESTADO DE CUENTA =
interface Movimiento {
  fecha: string; documento: string; concepto: string;
  debe: number; haber: number; saldo: number;
}
interface EstadoCuenta {
  cliente: { razon_social: string; identificacion: string } | null;
  contraparte: string; movimientos: Movimiento[]; saldo_final: number;
  desde: string | null; hasta: string | null;
}

const hoy = fechaLocal();
const inicioAnio = hoy.slice(0, 4) + '-01-01';

export function ReporteEstadoCuenta() {
  const [lado, setLado] = useState<'cxc' | 'cxp'>('cxc');
  const [ident, setIdent] = useState('');
  const [buscado, setBuscado] = useState('');
  const [desde, setDesde] = useState(inicioAnio);
  const [hasta, setHasta] = useState(hoy);
  const marca = useMarcaEmpresa();

  const { data, isFetching, error } = useQuery<EstadoCuenta>({
    queryKey: ['estado-cuenta', lado, buscado, desde, hasta],
    queryFn: () => finRpc<EstadoCuenta>(`${lado}_estado_cuenta`, {
      _identificacion: buscado, _desde: desde || null, _hasta: hasta || null,
    }),
    enabled: buscado.length >= 3,
  });

  const movs = useMemo(() => data?.movimientos ?? [], [data]);

  const imprimir = async () => {
    const win = window.open('', '_blank');
    if (!win) { toast.error('Permite las ventanas emergentes para imprimir el estado de cuenta'); return; }
    win.document.write('<p style="font-family:sans-serif;padding:2rem">Generando estado de cuenta…</p>');
    try {
      const d = await finRpc(`${lado}_estado_cuenta`, {
        _identificacion: buscado, _desde: desde || null, _hasta: hasta || null,
      });
      win.document.open();
      win.document.write(htmlEstadoCuenta(d));
      win.document.close();
    } catch {
      win.document.body.innerHTML = '<p style="font-family:sans-serif;padding:2rem">No se pudo generar el estado de cuenta.</p>';
    }
  };

  const headers = ['Fecha', 'Documento', 'Concepto', 'Debe', 'Haber', 'Saldo'];
  const filasExport = useMemo<Celda[][]>(
    () => movs.map((m) => [m.fecha, m.documento, m.concepto, m.debe, m.haber, m.saldo]),
    [movs],
  );

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-semibold">Estado de Cuenta</h1>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="flex flex-col text-xs text-muted-foreground">
            De
            <div className="mt-1 flex overflow-hidden rounded-md border">
              {([['cxc', 'Un cliente'], ['cxp', 'Un proveedor']] as const).map(([k, l]) => (
                <button key={k} onClick={() => { setLado(k); setBuscado(''); }}
                  className={`px-3 py-1.5 text-sm ${lado === k ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <label className="flex flex-col text-xs text-muted-foreground">RUC o cédula
            <input className={`${campo} mt-1 w-52`} value={ident} placeholder="1790898954001"
              onChange={(e) => setIdent(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setBuscado(ident.trim()); }} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Desde
            <input type="date" className={`${campo} mt-1 w-40`} value={desde} onChange={(e) => setDesde(e.target.value)} />
          </label>
          <label className="flex flex-col text-xs text-muted-foreground">Hasta
            <input type="date" className={`${campo} mt-1 w-40`} value={hasta} onChange={(e) => setHasta(e.target.value)} />
          </label>
          <Button size="sm" onClick={() => setBuscado(ident.trim())} disabled={ident.trim().length < 3}>
            Consultar
          </Button>
          <div className="ml-auto flex items-end gap-2 pb-1">
            <ExportarBotones
              nombre={`estado-cuenta-${buscado || 's-d'}-${hasta}`}
              titulo={`Estado de cuenta · ${data?.cliente?.razon_social ?? ''}`}
              subtitulo={`${desde} a ${hasta}`}
              headers={headers} filas={filasExport} colsDerecha={[3, 4, 5]}
              marca={marca}
              fmtPDF={(v, c) => (c < 3 ? String(v ?? '') : usd(v))}
              disabled={isFetching || movs.length === 0} />
            <Button size="sm" variant="outline" disabled={!data?.cliente} onClick={() => void imprimir()}
              title="Documento con membrete, listo para enviar al cliente">
              <Printer className="mr-1 h-4 w-4" /> Imprimir
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            {data?.cliente
              ? <>{data.cliente.razon_social}
                  <span className="font-mono text-xs font-normal text-muted-foreground">{data.cliente.identificacion}</span></>
              : <span className="text-muted-foreground">Sin consulta</span>}
            {isFetching && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
            {data && (
              <span className="ml-auto text-sm font-normal">
                Saldo al {hasta}: <b className={data.saldo_final < 0 ? 'text-destructive' : ''}>{usd(data.saldo_final)}</b>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {!!error && <p className="py-6 text-center text-sm text-destructive">{getErrorMessage(error)}</p>}
          {!buscado && !error && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Escribe el RUC o la cédula {lado === 'cxc' ? 'del cliente' : 'del proveedor'} y pulsa Consultar.
            </p>
          )}
          {buscado && !isFetching && !error && movs.length === 0 && (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Sin movimientos en el período para esa identificación.
            </p>
          )}
          {movs.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-left">Fecha</th>
                  <th className="py-2 pr-3 text-left">Documento</th>
                  <th className="py-2 pr-3 text-left">Concepto</th>
                  <th className="px-3 py-2 text-right">Debe</th>
                  <th className="px-3 py-2 text-right">Haber</th>
                  <th className="border-l px-3 py-2 text-right">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {movs.map((m, i) => (
                  <tr key={i} className="border-b last:border-0">
                    <td className="whitespace-nowrap py-1.5 pr-3">{m.fecha}</td>
                    <td className="whitespace-nowrap py-1.5 pr-3 font-mono text-xs">{m.documento}</td>
                    <td className="py-1.5 pr-3">{m.concepto}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{m.debe ? usd(m.debe) : ''}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{m.haber ? usd(m.haber) : ''}</td>
                    <td className="border-l px-3 py-1.5 text-right font-medium tabular-nums">{usd(m.saldo)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
